import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { CardReaderDisplayMessage } from 'Src/feature/card-reader/CardReaderDisplayMessage';
import { CardReaderPaymentMessage } from 'Src/feature/card-reader/CardReaderPaymentMessage';
import CheckActivityRow from 'Src/feature/check/CheckActivityRow';
import { Divider } from 'Src/feature/divider/Divider';
import { GiftCardBalanceApplyModal } from 'Src/feature/gift-card/GiftCardBalanceApplyModal';
import { GiftCardLookupModal } from 'Src/feature/gift-card/GiftCardLookupModal';
import { CheckPaymentCompleteModal } from 'Src/feature/payment/CheckPaymentCompleteModal';
import { CheckPaymentMethodModal } from 'Src/feature/payment/CheckPaymentMethodModal';
import useRoute from 'Src/routes/useRoute';
import { buildKeyFromObject } from 'Src/utils/object';

import { ChangeDueModal } from './ChangeDueModal';
import { CustomCashModal } from './CustomCashModal';
import { DuplicateCardConfirmationModal } from './DuplicateCardConfirmationModal';
import { EvenSplitModal } from './EvenSplitModal';
import { ExecuteModal } from './ExecuteModal';
import { OtherPaymentModal } from './OtherPaymentModal';
import { PartialPaymentModal } from './PartialPaymentModal';
import { useCheckPagePayment } from './useCheckPagePayment';

import styles from './CheckPagePayment.module.scss';

const sectionId = 'CheckPagePayment';

const CheckPagePayment: FC = () => {
  const { t } = useTranslation();
  const { navigate, crumbs } = useRoute();
  const store = useCheckPagePayment();

  return (
    <>
      <Flex data-testid={sectionId} className={styles['root']} gap="sm">
        {(store.currentCheck.query.data?.balance.unpaid === 0 || store.currentCheck.query.data?.status === 'Closed') ? (
          <>
            <Flex className={styles['activity-root']} gap="sm">
              <Divider variant="stubby" />
              <Copy variant="h6" value={t('check.activity')} />
              <Flex gap="xs" className={styles['activity']}>
                {store.currentCheck.query.data?.activities.map((activity) => (
                  <CheckActivityRow key={buildKeyFromObject(sectionId, activity)} activity={activity} check={store.currentCheck.query.data} />
                ))}
              </Flex>
            </Flex>
            <Divider variant="stubby" />
            <Copy variant="h6" value={t('check.labels.checkOptions')} />
            <div className={styles['grid-col4']}>
              {store.currentCheck.query.data?.status === 'Closed' && (
                <CTA
                  data-testid={`${sectionId}-reopen-check-btn`}
                  label={t('labels.reopenCheck')}
                  onClick={() => void store.reopenCheck()}
                />
              )}
              <CTA
                data-testid={`${sectionId}-reprint-receipts-btn`}
                label={t('labels.reprintReceipts')}
                onClick={async () => {
                  const paymentReceiptPromises = [store.printGuestReceipt(), ...store.currentCheck.query.data?.payments
                    .filter((pmt) => !['Declined', 'Removed', 'TACReq'].includes(pmt.paymentStatus))
                    .map(async (pmt) => await store.printPaymentReceipt(pmt.id)) ?? []
                  ];
                  await Promise.all(paymentReceiptPromises);
                }}
              />
              <CTA
                data-testid={`${sectionId}-adjust-tips-btn`}
                label={t('payment.labels.adjustTips')}
                onClick={() => {
                  navigate(crumbs.pos.tipEdit.main.interpolate({}, { checkNumber: store.currentCheck.query.data?.number }));
                }}
              />
            </div>
          </>
        ) : (
          <>
            <Flex>
              {store.can.doCardReaderPayment && (
                <>
                  <CardReaderPaymentMessage />
                  <CardReaderDisplayMessage />
                </>
              )}
              <Copy
                variant="h4"
                align="center"
                value={t('labels.util.currency', { value: store.currentCheck.query.data?.balance.unpaid ?? 0 })}
              />
              {store.currentCheck.query.data?.balance.paid !== 0 && <Copy variant="body" size="sm" color="textSecondary" value={t('payment.labels.amountPaidVsTotal', { paid: store.currentCheck.query.data?.balance.paid ?? 0, total: store.currentCheck.query.data?.balance.total ?? 0 })} align="center" />}
              {store.can.doSplitOrPartialPayment && (
                <div className={styles['grid-col2']}>
                  <CTA
                    data-testid={`${sectionId}-partial-payment-btn`}
                    variant="outlined"
                    label={t('payment.labels.partialPayment')}
                    onClick={store.modal.part.open}
                  />
                  <CTA
                    data-testid={`${sectionId}-split-evenly-btn`}
                    variant="outlined"
                    label={t('payment.labels.splitEvenly')}
                    onClick={store.modal.split.open}
                  />
                </div>
              )}
            </Flex>
            <Flex grow justifyContent="flex-end">
              <Divider variant="stubby" />
              <Copy variant="h6" value={t('otherPaymentOptions.label_plural')} />
              <div className={styles['grid-col4']}>
                {store.can.doSplitCheck && (
                  <CTA
                    data-testid={`${sectionId}-split-check-btn`}
                    label={t('checkSplit.label')}
                    onClick={store.onSplitCheckClick}
                  />
                )}
                {store.can.doGiftCardPayment && (
                  <CTA
                    data-testid={`${sectionId}-gift-card-lookup-btn`}
                    label={t('labels.giftCardLookup')}
                    onClick={() => store.paymentFlow.do('gc-lookup-start')}
                  />
                )}
                {store.can.doManualPayment && (
                  <CTA
                    data-testid={`${sectionId}-manual-cc-btn`}
                    label={t('labels.manualCreditCard')}
                    onClick={() => {
                      void store.manual.onPay();
                    }}
                  />
                )}
                <CTA
                  data-testid={`${sectionId}-reprint-guest-check-btn`}
                  label={t('labels.reprintGuestCheck')}
                  onClick={() => void store.printGuestReceipt()}
                />
                {store.can.doOtherPayment && (
                  <CTA
                    data-testid={`${sectionId}-other-btn`}
                    label={t('labels.other')}
                    onClick={store.modal.otherPayment.open}
                  />
                )}
              </div>
              {store.can.doCashPayment && (
                <>
                  <Divider variant="stubby" />
                  <Copy variant="h6" value={t('labels.cash')} />
                  <div className={styles['grid-col5']}>
                    <CTA
                      data-testid={`${sectionId}-cash-exact-change-btn`}
                      label={t('payment.labels.exact', { amount: store.currentCheck.query.data?.balance.cashDiscountedTotal ?? store.currentCheck.query.data?.balance.unpaid ?? 0 })}
                      onClick={() => {
                        store.cash.onPay(store.currentCheck.query.data?.balance.cashDiscountedTotal ?? store.currentCheck.query.data?.balance.unpaid ?? 0);
                      }}
                      disabled={store.cash.isPending}
                    />
                    {Array.from(store.cash.buttons).map(([idx, val]) => (
                      <CTA
                        key={idx}
                        data-testid={`${sectionId}-cash-${val}-btn`}
                        label={t('payment.labels.dynamicAmount', { amount: val })}
                        onClick={() => {
                          store.cash.onPay(val);
                        }}
                        disabled={store.cash.isPending}
                      />
                    ))}
                    <CTA
                      data-testid={`${sectionId}-cash-custom-amount-btn`}
                      label={t('payment.labels.customAmount')}
                      onClick={store.modal.customCashAmount.open}
                      disabled={store.cash.isPending}
                    />
                  </div>
                </>
              )}
            </Flex>
          </>
        )}
      </Flex>

      <ChangeDueModal control={store.modal.changeDue} />
      <CustomCashModal control={store.modal.customCashAmount} />
      <OtherPaymentModal control={store.modal.otherPayment} />
      <PartialPaymentModal control={store.modal.part} />
      <EvenSplitModal control={store.modal.split} />
      <ExecuteModal control={store.modal.execute} />
      <GiftCardLookupModal control={store.modal.giftCardLookup} />
      <GiftCardBalanceApplyModal control={store.modal.giftCardBalanceApply} />

      <CheckPaymentMethodModal data-testid="cc-pay-method-modal" control={store.modal.ccMethod} />
      <CheckPaymentMethodModal data-testid="gc-pay-method-modal" control={store.modal.giftCardMethodPayment} />
      <CheckPaymentMethodModal data-testid="gc-lookup-pay-method-modal" control={store.modal.giftCardMethodLookup} />
      <CheckPaymentMethodModal data-testid="manual-cc-pay-method-modal" control={store.modal.manualMethod} />

      <CheckPaymentCompleteModal data-testid="pay-complete-modal" control={store.modal.payComplete} />
      <DuplicateCardConfirmationModal data-testid="duplicate-card-confirmation-modal" control={store.modal.duplicateCard} />
    </>
  );
};

export { CheckPagePayment, sectionId };
